<template>
    <div>
        <checkbox v-model="includeInactive">Include Inactive</checkbox>

        <b-table :items="filteredQuestionSets" :fields="fields">
            <template #cell(active)="data">
                {{ data.value ? "✔️" : "" }}
            </template>
            <template #cell(actions)="data">
                <b-button variant="link" @click.prevent="startEdit(data.item)" title="Edit">
                    <b-icon icon="pencil" variant="primary"></b-icon>
                </b-button>
            </template>
        </b-table>

        <b-button variant="primary" size="sm" @click="newSet">
            New Question Set...
        </b-button>

        <b-modal :visible="!!selectedQuestionSet" no-fade title="Question Set" @hidden="selectedQuestionSet = null"
            size="lg" ok-title="Save Question Set" scrollable @ok="saveSet">
            <template v-if="selectedQuestionSet">
                <div class="d-flex" style="gap: 15px;">
                    <b-form-group label="Question Set Name" class="flex-fill">
                        <b-input v-model="selectedQuestionSet.name" />
                    </b-form-group>
                    <b-form-group label="Active" class="text-center">
                        <b-check v-model="selectedQuestionSet.active" switch />
                    </b-form-group>
                </div>
                <div v-if="defaultScopeOptions?.length">
                    <b-form-group :label="'Use As Default For ' + defaultScopeName">
                        <b-select v-model="selectedQuestionSet.defaultScope" :options="['', ...defaultScopeOptions]" />
                    </b-form-group>
                </div>
                <hr>
                <TaskQuestions :questions="selectedQuestionSet.questions"
                    @input="updateQuestionSet"
                    :editing="true"
                    :show-errors="showQuestionErrors"
                    :entity-type="entityType" />
            </template>
        </b-modal>
    </div>
</template>

<script lang="ts">
import { EntityType } from '@/models/EntityType';
import { UserTaskQuestionSet, UserTaskQuestionType } from '@/models/UserTask';
import { Component, Prop, Vue } from 'vue-property-decorator';
import TaskQuestions from './TaskQuestions.vue';
import { BvModalEvent } from 'bootstrap-vue';
import Checkbox from "@/components/Inputs/Checkbox.vue";
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

@Component({
    name: "TaskQuestionSets",
    components: {
        TaskQuestions,
        Checkbox
    }
})
export default class TaskQuestionSets extends Vue {

    @Prop({ default: EntityType.UserTask }) protected entityType!: EntityType;
    @Prop({}) protected defaultScopeOptions?: any[];
    @Prop({}) protected defaultScopeName?: string;

    protected includeInactive = false;

    protected selectedQuestionSet: UserTaskQuestionSet | null = null;

    protected showQuestionErrors = false;

    mounted() {
        this.getQuestionSets();
    }

    protected questionSets: UserTaskQuestionSet[] = [];

    get anySetHasDefaultScope() {
        return this.questionSets?.some(qs => qs.defaultScope);
    }

    get fields() {
        return [
            { key: 'name' },
            { key: 'defaultScope', label: `Default for ${this.defaultScopeName}`, hidden: !this.anySetHasDefaultScope },
            { key: 'active', class: "active" },
            { key: 'actions', label: "", class: "actions" }
        ].filter(f => f.hidden !== true)
    }

    async getQuestionSets() {
        try {
            const resp = await this.$http.get<UserTaskQuestionSet[]>(`/task/question-sets/${this.entityType}`);
            this.questionSets = resp.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    newSet() {
        this.selectedQuestionSet = {
            id: 0,
            name: "",
            entityType: this.entityType,
            created: new Date(),
            createdBy: "",
            active: true,
            questions: [],
            defaultScope: ""
        };
    }

    updateQuestionSet(updatedQuestions: any[]) {
        if (this.selectedQuestionSet) {
            this.selectedQuestionSet.questions = updatedQuestions;
        }
    }

    async saveSet(e: BvModalEvent) {
        e.preventDefault();

        if (!this.selectedQuestionSet?.questions.length) {
            this.$notification(NotificationOptions.error("Must define at least one question."));
            return;
        }

        if (!this.selectedQuestionSet.name?.trim()) {
            this.$notification(NotificationOptions.error("Must enter a name for this question set."));
            return;
        }

        this.showQuestionErrors = false;

        const questionsHaveErrors = this.selectedQuestionSet?.questions.some(q => {
            if (!q.text?.trim()) return true;
            if (![UserTaskQuestionType.multipleChoiceMultiple, UserTaskQuestionType.multipleChoiceSingle].includes(q.typeId)) return false;
            if (q.options.some(o => !o.text?.trim())) return true;
            const uniqueTexts = new Set(q.options.map(o => o.text?.trim()));
            if (uniqueTexts.size !== q.options.length) return true;
            return false;
        });

        if (questionsHaveErrors) {
            this.$nextTick(() => {
                this.showQuestionErrors = true;
            });
            this.$notification(NotificationOptions.error("Some questions have errors."));
            return false;
        }

        try {
            await this.$http.post("/task/question-set", this.selectedQuestionSet);
            this.getQuestionSets();
            this.selectedQuestionSet = null;
            this.$notification(NotificationOptions.success("Successfully saved question set."));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    async startEdit(set: UserTaskQuestionSet) {
        try {
            const resp = await this.$http.get<UserTaskQuestionSet>(`/task/question-set/${set.id}`);
            this.selectedQuestionSet = resp.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    get filteredQuestionSets() {
        let sets = this.questionSets.filter(qs => qs.name);

        if (!this.includeInactive) {
            sets = sets.filter(qs => qs.active);
        }

        return sets;
    }

}

</script>

<style scoped>
::v-deep(table .actions),
::v-deep(table .active) {
    width: 50px;
}
</style>